import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BookmarkPlus, Check, ArrowRight } from "lucide-react";
import { getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { claimResult, getResultClaim, removeResultClaim } from "../../lib/resultClaimsApi";
import "./ResultClaimControl.css";

export default function ResultClaimControl({ family, matchId, participantId, competitorName, eligible = true }) {
  const location = useLocation();
  const [user, setUser] = useState(undefined);
  const [state, setState] = useState(null);
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const actionRef = useRef(null);
  const feedbackRef = useRef(null);
  const targetKey = `${family}:${matchId}:${participantId}`;
  const key = `${targetKey}:${user?.userId || "guest"}`;
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    let active = true;
    let authRequest = 0;
    async function refresh() {
      const current = ++authRequest;
      ++generation.current;
      setUser(undefined);
      setState(null);
      try {
        const value = await getCurrentUser();
        if (active && current === authRequest) setUser(value?.userId ? value : null);
      } catch { if (active && current === authRequest) setUser(null); }
    }
    const stop = Hub.listen("auth", ({ payload }) => {
      if (["signedOut", "tokenRefresh_failure"].includes(payload.event)) {
        ++authRequest;
        ++generation.current;
        setState(null);
        setUser(null);
      } else if (payload.event === "signedIn") refresh();
    });
    refresh();
    function invalidate() { ++generation.current; }
    return () => { active = false; ++authRequest; invalidate(); stop(); };
  }, []);

  useEffect(() => {
    const current = ++generation.current;
    if (!user?.userId || !participantId || !matchId || !eligible) return undefined;
    const controller = new AbortController();
    setState({ key, loading: true });
    getResultClaim({ family, matchId, participantId, expectedUserId: user.userId, signal: controller.signal })
      .then(item => { if (current === generation.current) setState({ key, item }); })
      .catch(error => {
        if (current === generation.current && error.name !== "AbortError") setState({ key, error: "Your claim status could not load. Scores are still available.", expired: error.status === 401 });
      });
    function invalidate() { ++generation.current; }
    return () => { invalidate(); controller.abort(); };
  }, [family, matchId, participantId, eligible, user, key, retry]);

  const current = state?.key === key ? state : null;
  const item = current?.item;
  const signInPath = buildAuthReturnPath("/auth/sign-in", buildCurrentReturnTo(location));
  const identityMissing = !participantId || !matchId;
  const unavailable = item && item.availability !== "available";
  useEffect(() => {
    if (current?.message) (actionRef.current || feedbackRef.current)?.focus();
  }, [current?.message]);

  async function updateClaim(remove = false) {
    const request = ++generation.current;
    const requestKey = key;
    setState({ key, item, busy: true });
    try {
      const target = { family, matchId, participantId, expectedUserId: user.userId };
      const updated = await (remove ? removeResultClaim(target) : claimResult(target));
      if (request === generation.current && requestKey === keyRef.current) {
        setState({ key, item: remove ? { ...item, claimed: false } : updated, message: remove ? "Claim removed. Published results are unchanged." : "Result added to your past matches." });
      }
    } catch (error) {
      if (request === generation.current && requestKey === keyRef.current) {
        const unavailableItem = error.status === 409 && typeof error.data?.claimed === "boolean" ? error.data : null;
        setState({ key, item: unavailableItem || item, error: error.status === 401 ? "Your session has expired. Sign in to continue." : unavailableItem ? "This result is no longer available to claim." : remove ? "The claim could not be removed. Try again." : "The claim could not be saved. Try again.", expired: error.status === 401 });
      }
    }
  }

  return <section className="result-claim" aria-label="Personal result history" aria-busy={Boolean(current?.busy)}>
    <div className="result-claim-copy">
      <strong>{item?.claimed ? <><Check size={16} aria-hidden="true" /><span className="result-claim-badge">Claimed</span></> : item?.alreadyRegistered ? "Already in My Matches" : "Is this your result?"}</strong>
      <p>{identityMissing ? "This result is missing a linked participant and cannot be claimed yet."
        : !eligible ? "Results can be claimed after the match ends."
          : unavailable ? item.availability === "match_unavailable" ? "Match no longer available." : "Result no longer available."
            : item?.alreadyRegistered ? "Your registration already connects this result to your account."
              : item?.claimed ? "Saved in your personal history. Published results remain unchanged."
                : <>Add this result for <b>{competitorName || "this competitor"}</b> to your past matches. Select only your own result.</>}</p>
    </div>
    {!identityMissing && eligible && <div className="result-claim-actions">
      {user === undefined || (user && (!current || current.loading)) ? <span className="result-claim-pending">Checking your history…</span>
        : !user || current?.expired ? <Link className="result-claim-primary" to={signInPath}>Sign in to claim this result<ArrowRight size={16} aria-hidden="true" /></Link>
          : item && <>
            {(item.claimed || item.alreadyRegistered) && <Link ref={actionRef} className="result-claim-primary" to="/matches/my#mm-past">View in My Matches<ArrowRight size={16} aria-hidden="true" /></Link>}
            {item.claimed ? <button className="result-claim-secondary" type="button" disabled={current.busy} onClick={() => updateClaim(true)}>{current.busy ? "Removing…" : "Remove claim"}</button>
              : !unavailable && !item.alreadyRegistered && <button ref={actionRef} className="result-claim-primary" type="button" disabled={current.busy} onClick={() => updateClaim()}><BookmarkPlus size={17} aria-hidden="true" />{current.busy ? "Claiming…" : "Claim my result"}</button>}
          </>}
    </div>}
    {current?.message && <p ref={feedbackRef} tabIndex={-1} className="result-claim-feedback" role="status">{current.message}</p>}
    {current?.error && <div className="result-claim-feedback result-claim-error" role="alert"><p>{current.error}</p>{!current.expired && <button type="button" className="result-claim-secondary" onClick={() => setRetry(value => value + 1)}>Retry claim status</button>}</div>}
  </section>;
}
